import React, { useState, useEffect } from "react";
import { Col, Input, Row, Form, message } from "antd";
import { ReactComponent as SaveIcon } from "../../../assets/save-icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import { setProjectWeightsIsEdit, updateProjectWeights, updateProject } from "../redux/actions";
import SpinnerComponent from "../../../components/spinner";
import "./projectSections.css";

const ProjectWeightsSection = ({ isProjectUpdate, canUpdate }) => {
  const [isEdit, setIsEdit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [materialsTotalWeighting, setMaterialsTotalWeighting] = useState(0);
  const [boltsTotalWeighting, setBoltsTotalWeighting] = useState(0);
  const [materialsValidTotal, setMaterialsValidTotal] = useState(true);
  const [boltsValidTotal, setBoltsValidTotal] = useState(true);
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const {
    updateWeightsLoading,
    currentProjectObj,
    projectWeightsIsEdit,
  } = useSelector((state) => state.projectsSetupReducer);

  useEffect(() => {
    setIsEdit(projectWeightsIsEdit);
  }, [projectWeightsIsEdit]);

  useEffect(() => {
    setLoading(updateWeightsLoading);
  }, [updateWeightsLoading]);

  useEffect(() => {
    if (currentProjectObj) {
      const formValues = {
        materialFabrication: currentProjectObj.materialFabrication || "",
        materialQcRelease: currentProjectObj.materialQcRelease || "",
        materialCorrosionProtection: currentProjectObj.materialCorrosionProtection || "",
        materialDelivery: currentProjectObj.materialDelivery || "",
        materialSiteReceipt: currentProjectObj.materialSiteReceipt || "",
        materialsTotalWeightings: currentProjectObj.materialsTotalWeightings || 0,
        boltsFabrication: currentProjectObj.boltsFabrication || "",
        boltsQcRelease: currentProjectObj.boltsQcRelease || "",
        boltsDeliveryToWorkshop: currentProjectObj.boltsDeliveryToWorkshop || "",
        boltsDeliveryToSite: currentProjectObj.boltsDeliveryToSite || "",
        boltsSiteReceipt: currentProjectObj.boltsSiteReceipt || "",
        boltsTotalWeighting: currentProjectObj.boltsTotalWeighting || 0
      };
      form.setFieldsValue(formValues);
      
      // Calculate initial totals
      calculateMaterialsTotal(formValues);
      calculateBoltsTotal(formValues);
    }
  }, [currentProjectObj, form]);
  
  // Calculate materials total weighting
  const calculateMaterialsTotal = (values) => {
    // Get raw values for each field
    const materialValues = [
      parseFloat(values.materialFabrication) || 0,
      parseFloat(values.materialQcRelease) || 0,
      parseFloat(values.materialCorrosionProtection) || 0,
      parseFloat(values.materialDelivery) || 0,
      parseFloat(values.materialSiteReceipt) || 0
    ];
    
    // Sum up the raw values
    const materialTotal = materialValues.reduce((sum, val) => sum + val, 0);
    
    // Round to 1 decimal place for display
    const roundedTotal = Math.round(materialTotal * 10) / 10;
    
    // Check if total is valid (must equal exactly 100)
    const isValid = roundedTotal === 100;
    setMaterialsValidTotal(isValid);
    
    setMaterialsTotalWeighting(roundedTotal);
    return roundedTotal;
  };
  
  // Calculate bolts total weighting
  const calculateBoltsTotal = (values) => {
    // Get raw values for each field
    const boltValues = [
      parseFloat(values.boltsFabrication) || 0,
      parseFloat(values.boltsQcRelease) || 0,
      parseFloat(values.boltsDeliveryToWorkshop) || 0,
      parseFloat(values.boltsDeliveryToSite) || 0,
      parseFloat(values.boltsSiteReceipt) || 0
    ];
    
    // Sum up the raw values
    const boltTotal = boltValues.reduce((sum, val) => sum + val, 0);
    
    // Round to 1 decimal place for display
    const roundedTotal = Math.round(boltTotal * 10) / 10;
    
    // Check if total is valid (must equal exactly 100)
    const isValid = roundedTotal === 100;
    setBoltsValidTotal(isValid);
    
    setBoltsTotalWeighting(roundedTotal);
    return roundedTotal;
  };
  const handleSave = () => {
    // Get current form values
    const currentValues = form.getFieldsValue();
    
    // Calculate totals
    const materialTotal = calculateMaterialsTotal(currentValues);
    const boltTotal = calculateBoltsTotal(currentValues);
    
    // Only proceed if both totals are exactly 100%
    if (materialTotal === 100 && boltTotal === 100) {
      form.submit();
      // Only set isEdit to false if validation passes
      dispatch(setProjectWeightsIsEdit(false));
    } else {
      // Show error message
      message.error('Materials and Bolts weightings must equal exactly 100%');
    }
  };

  const onFinish = (values) => {
    // Calculate totals using the helper functions
    const materialTotal = calculateMaterialsTotal(values);
    const boltTotal = calculateBoltsTotal(values);
    
    // Check if either total is not exactly 100%
    if (materialTotal !== 100 || boltTotal !== 100) {
      message.error('Materials and Bolts weightings must equal exactly 100%');
      return;
    }
    
    // Check if either total is 0 (no weights entered)
    if (materialTotal === 0 || boltTotal === 0) {
      message.error('Please enter weights for both Materials and Bolts sections');
      return; // Prevent form submission if weights are missing
    }
    
    // Update the form with calculated totals
    form.setFieldsValue({
      materialsTotalWeightings: materialTotal,
      boltsTotalWeighting: boltTotal
    });
    
    dispatch(
      updateProject({
        materialFabrication: values.materialFabrication,
        materialQcRelease: values.materialQcRelease,
        materialCorrosionProtection: values.materialCorrosionProtection,
        materialDelivery: values.materialDelivery,
        materialSiteReceipt: values.materialSiteReceipt,
        materialsTotalWeightings: materialTotal,
        boltsFabrication: values.boltsFabrication,
        boltsQcRelease: values.boltsQcRelease,
        boltsDeliveryToWorkshop: values.boltsDeliveryToWorkshop,
        boltsDeliveryToSite: values.boltsDeliveryToSite,
        boltsSiteReceipt: values.boltsSiteReceipt,
        boltsTotalWeighting: boltTotal,
        id: currentProjectObj?.id // Make sure to include the project ID
      })
    );
  };
  
  // Handle input changes to immediately update totals
  const handleInputChange = (field, value) => {
    const currentValues = form.getFieldsValue();
    currentValues[field] = value;
    
    // Recalculate totals based on which field was changed
    if (field.startsWith('material')) {
      calculateMaterialsTotal(currentValues);
    } else if (field.startsWith('bolts')) {
      calculateBoltsTotal(currentValues);
    }
  };

  return (
    <>
      <Row>
        <Col span={24} className="heading-col">
          <Row align="middle" justify="space-between">
            <Col>
              <div>
                <span className="heading-span">PROJECT WEIGHTINGS</span>
              </div>
            </Col>
            <Col>
              {isEdit ? (
                loading ? (
                  <SpinnerComponent fontSize={14} />
                ) : (
                  <SaveIcon width={16} height={16} fill="#f25003" onClick={handleSave} />
                )
              ) : (
                canUpdate && (
                  <EditIcon
                    width={16}
                    fill="#f25003"
                    height={16}
                    onClick={() => dispatch(setProjectWeightsIsEdit(true))}
                  />
                )
              )}
            </Col>
          </Row>
        </Col>
        <Col
          span={24}
          className={`user-details-section project-details-section ${
            !isEdit && "is-edit"
          }`}
        >
          <Row justify="center">
            <Col md={24} lg={23} xl={20} xxl={16}>
              <Form
                form={form}
                name="project-weights"
                onFinish={onFinish}
                scrollToFirstError
                labelWrap={true}
                layout="horizontal"
                className="user-detail-form"
                requiredMark={false}
              >
                <Row gutter={[20, 10]}>
                  <Col span={12}>
                    <div className="weight-box">
                      <div className="weight-section-title">MATERIALS WEIGHTING</div>
                      <Row className="weight-header">
                        <Col span={12}>
                          <div className="weight-label">DESCRIPTION</div>
                        </Col>
                        <Col span={12}>
                          <div className="weight-label">WEIGHTING %</div>
                        </Col>
                      </Row>
                      
                      <Row className="weight-row">
                        <Col span={12}>
                          <div className="weight-item">Fabrication</div>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="materialFabrication" noStyle>
                            <Input 
                              type="number"
                              disabled={!isEdit || !canUpdate}
                              placeholder="0"
                              className="weight-input"
                              onChange={(e) => handleInputChange('materialFabrication', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      
                      <Row className="weight-row">
                        <Col span={12}>
                          <div className="weight-item">QC Release</div>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="materialQcRelease" noStyle>
                            <Input 
                              type="number"
                              disabled={!isEdit || !canUpdate}
                              placeholder="0"
                              className="weight-input"
                              onChange={(e) => handleInputChange('materialQcRelease', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      
                      <Row className="weight-row">
                        <Col span={12}>
                          <div className="weight-item">Corrosion Protection</div>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="materialCorrosionProtection" noStyle>
                            <Input 
                              type="number"
                              disabled={!isEdit || !canUpdate}
                              placeholder="0"
                              className="weight-input"
                              onChange={(e) => handleInputChange('materialCorrosionProtection', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      
                      <Row className="weight-row">
                        <Col span={12}>
                          <div className="weight-item">Delivery</div>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="materialDelivery" noStyle>
                            <Input 
                              type="number"
                              disabled={!isEdit || !canUpdate}
                              placeholder="0"
                              className="weight-input"
                              onChange={(e) => handleInputChange('materialDelivery', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      
                      <Row className="weight-row last-item">
                        <Col span={12}>
                          <div className="weight-item">Site Receipt</div>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="materialSiteReceipt" noStyle>
                            <Input 
                              type="number"
                              disabled={!isEdit || !canUpdate}
                              placeholder="0"
                              className="weight-input"
                              onChange={(e) => handleInputChange('materialSiteReceipt', e.target.value)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row className="weight-row total-row">
                        <Col span={12}><div className="weight-item bold">Total Weighting</div></Col>
                        <Col span={12}>
                          <div 
                            className={`weight-total bold ${!materialsValidTotal ? 'error-text' : ''}`}
                            style={{ color: !materialsValidTotal ? '#ff4d4f' : 'inherit' }}
                          >
                            {materialsTotalWeighting}%
                            {!materialsValidTotal && 
                              <span style={{ marginLeft: '5px', fontSize: '12px' }}>
                                (Must equal 100%)
                              </span>
                            }
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="weight-box">
                      <div className="weight-section-title">BOLTS WEIGHTING</div>
                      <Row className="weight-header">
                      <Col span={12}>
                        <div className="weight-label">DESCRIPTION</div>
                      </Col>
                      <Col span={12}>
                        <div className="weight-label">WEIGHTING %</div>
                      </Col>
                    </Row>
                    
                    <Row className="weight-row">
                      <Col span={12}>
                        <div className="weight-item">Fabrication</div>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="boltsFabrication" noStyle>
                          <Input 
                            type="number"
                            disabled={!isEdit || !canUpdate}
                            placeholder="0"
                            className="weight-input"
                            onChange={(e) => handleInputChange('boltsFabrication', e.target.value)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row className="weight-row">
                      <Col span={12}>
                        <div className="weight-item">QC Release</div>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="boltsQcRelease" noStyle>
                          <Input 
                            type="number"
                            disabled={!isEdit || !canUpdate}
                            placeholder="0"
                            className="weight-input"
                            onChange={(e) => handleInputChange('boltsQcRelease', e.target.value)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row className="weight-row">
                      <Col span={12}>
                        <div className="weight-item">Delivery to Workshop</div>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="boltsDeliveryToWorkshop" noStyle>
                          <Input 
                            type="number"
                            disabled={!isEdit || !canUpdate}
                            placeholder="0"
                            className="weight-input"
                            onChange={(e) => handleInputChange('boltsDeliveryToWorkshop', e.target.value)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row className="weight-row">
                      <Col span={12}>
                        <div className="weight-item">Delivery to Site</div>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="boltsDeliveryToSite" noStyle>
                          <Input 
                            type="number"
                            disabled={!isEdit || !canUpdate}
                            placeholder="0"
                            className="weight-input"
                            onChange={(e) => handleInputChange('boltsDeliveryToSite', e.target.value)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row className="weight-row last-item">
                      <Col span={12}>
                        <div className="weight-item">Site Receipt</div>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="boltsSiteReceipt" noStyle>
                          <Input 
                            type="number"
                            disabled={!isEdit || !canUpdate}
                            placeholder="0"
                            className="weight-input"
                            onChange={(e) => handleInputChange('boltsSiteReceipt', e.target.value)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row className="weight-row total-row">
                        <Col span={12}><div className="weight-item bold">Total Weighting</div></Col>
                        <Col span={12}>
                          <div 
                            className={`weight-total bold ${!boltsValidTotal ? 'error-text' : ''}`}
                            style={{ color: !boltsValidTotal ? '#ff4d4f' : 'inherit' }}
                          >
                            {boltsTotalWeighting}%
                            {!boltsValidTotal && 
                              <span style={{ marginLeft: '5px', fontSize: '12px' }}>
                                (Must equal 100%)
                              </span>
                            }
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="empty-div"></div>
    </>
  );
};

export default ProjectWeightsSection;
